import { CommonTooltips, ImageTable } from "@mcleod/common";
import { BlurEvent, Button, ChangeEvent, ClickEvent, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, Label, Layout, LookupModelSearchEvent, LookupModelSelectionEvent, OpenType, Panel, SlideoutDecorator, Snackbar, Switch, Tab, TableRow, TableRowDisplayEvent, TableRowDisplayListener, TableRowMode, TableRowModeChangeListener, Textbox, Toast } from "@mcleod/components";
import { TextboxValidator } from "@mcleod/components/src/components/textbox/TextboxValidator";
import { ResetFormButton } from "@mcleod/components/src/page/ResetFormButton";
import {
    ActionPermission,
    Alignment,
    Api,
    ArrayUtil,
    CurrencySettings,
    DateRange,
    DisplayType,
    DisplayValue,
    LayoutUrl,
    LicenseTester,
    McLeodClassicIntegration,
    Model,
    ModelRow,
    ModelSearchResult,
    Navigation,
    PermissionsUtil,
    StringUtil
} from "@mcleod/core";
import { AssignCarrierPlanPrompt, AvailableLoadsList, ModelCarrierPlan } from "@mcleod/crm";
import { getDispatchControlBoolean, LMECompanySettings, ResponsibilityUtil, UsersResponsibilitySlideoutMF } from "@mcleod/general";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutCarrier } from "./autogen/AutogenLayoutCarrier";
import { CarrierOverride } from "./CarrierOverride";
import { CarrierQualification } from "./CarrierQualification";
import { LaneHistory } from "./LaneHistory";
import { LanePreferenceContactManager } from "./LanePreferenceContactManager";
import { QualificationDetailSlideout } from "./QualificationDetailSlideout";
import {ModelBrokerageTrackingCtrl, ModelCarrierInsCtrl} from "@mcleod/powerbroker";

type InsuranceInfo = {
    insurer_name: string,
    insurer_phone: string,
    insurance_policy_number: string,
    insurance_amount: number,
    expiration_date: string
}

export class Carrier extends AutogenLayoutCarrier {
    private static viewCarrierURL = "lme/dispatch/Carrier?mode=update&key=";
    private static cimVendorUrlPromise: Promise<void> | null = null;
    private _ovrrdTimeoutHandle: number;
    private lanePreferenceDisplayListener: TableRowDisplayListener;
    private lanePreferenceRowModeChangeListener: TableRowModeChangeListener;
    private add_carrier_vendors = ['D', 'H', 'R'];
    private contactManager: LanePreferenceContactManager;
    private macroPointFieldsSet: boolean = false;

    override async onLoad(): Promise<void> {

        this.textboxId.lookupModel = null;
        this.textboxId.lookupModelLayout = null;
        this.sourceCarrier.afterGetDataboundValues = (row: ModelRow, dataSource: DataSource) => this.sourceCarrierAfterGetDataboundValues(row, dataSource);

        const currencySettings = CurrencySettings.getSingleton();
        const multiCurrencyEnabled = currencySettings.multiCurrencyEnabled();
        this.textboxPayeeCurrency.visible = this.textboxPayeeCurrency.required = multiCurrencyEnabled;

        const companySettings = LMECompanySettings.getSingleton();
        if (companySettings.isBrokerageLtl()) {
            const modelPath = "lme/powerbroker/contract-management-control";
            const responseP44 = await Model.searchSingleRecord(modelPath, { id: "P44" });
            this.textboxP44ProfileId.visible = responseP44 != null && responseP44.get("is_active") == "Y";
            const responseSLA = await Model.searchSingleRecord(modelPath, { id: "SLA" });
            const isActive = responseSLA != null && responseSLA.get("is_active") == "Y";
            this.textboxSMC3ProfileId.visible = isActive;
            this.textboxSmc3AccountNo.visible = isActive;
        }
        this.layoutTableContact.setCarrierContactColumnsVisible(true);
        this.styleTab(this.tabPnnOptions);

        this.contactManager = new LanePreferenceContactManager({
            lanePreferenceTable: this.layoutLanePreferenceTable.tableLanes,
            sendToContactsTable: this.layoutLaneSendToContacts.tableSendToContacts,
            carrierContactsTable: this.layoutTableContact.tableContacts,
            parentDataSource: this.mainDataSource
        })

        this.layoutQualification.buttonQualDetails.visible = true;
        this.layoutQualification.buttonQualDetails.addClickListener(event => this.buttonQualDetailsOnClick(event));
        const totalPointsLabel = new Label({
            text: "Total Score: ",
            rowBreak: false,
        });
        const totalPointsTextbox = new Textbox({
            dataSource: this.sourceCarrierPerfCalculation,
            field: "performance_score",
            id: "textboxTotalPoints",
            rowBreak: false,
            captionVisible: false,
            printable: true,
        });
        this.tabScorecardDetails.heading.insert(totalPointsLabel, this.tabScorecardDetails.heading.indexOf(this.tabScorecardDetails.headingLabel) + 1);
        this.tabScorecardDetails.heading.insert(totalPointsTextbox, this.tabScorecardDetails.heading.indexOf(totalPointsLabel) + 1);

        const isLogixOnboardingLicensed =
            LicenseTester.testPackage("com.tms.client.loadmaster.logixsolutions.onboarding");

        const isLogixAcceleratorLicensed = LicenseTester.testClass(
            "com.tms.client.loadmaster.logixsolutions.onboarding.PanelDrsPayeeBillingAccelerator"
        );

        const isLogixLicensed = isLogixOnboardingLicensed || isLogixAcceleratorLicensed;

        this.tabLogix.visible = isLogixLicensed;
        this.tabLogix.enabled = isLogixLicensed;

        if(isLogixLicensed){
            this.tabBillingAccelerator.visible = isLogixAcceleratorLicensed;
            this.tabBillingAccelerator.enabled = isLogixAcceleratorLicensed;

            this.tabLogixOnboarding.visible = isLogixOnboardingLicensed;
            this.tabLogixOnboarding.enabled = isLogixOnboardingLicensed;
            if(isLogixOnboardingLicensed && !isLogixAcceleratorLicensed)
                this.tabsetLogixSolutions.selectedIndex = this.tabsetLogixSolutions.indexOf(this.tabLogixOnboarding);
        }

        this.setRecurringTractorLastCreatedTooltip();
        this.sourceCarrier.addAfterModeChangeListener(async () => {
            if (this.sourceCarrier.mode == DataSourceMode.SEARCH) {
                this.buttonCarrierUsers.visible = await ResponsibilityUtil.isFilterEnabled("carr_agency_filter");
            }
            if (this.sourceCarrier.mode == DataSourceMode.ADD) {
                this.buttonCarrierUsers.visible = false;
            }
            if (this.sourceCarrier.mode == DataSourceMode.UPDATE) {
                this.buttonCarrierUsers.visible = await ResponsibilityUtil.isFilterEnabled("carr_agency_filter");
            }
        });
    }

    displayCarrierUsersSlideout() {
        const urs = UsersResponsibilitySlideoutMF.builder()
            .layoutName("lme/dispatch/CarrierUsers")
            .title(this.getCarrierUsersSlideoutTitle())
            .idFilter(this.getCarrierUsersIdFilter())
            .parentMainDataSource(this.sourceCarrier)
            .parentAgencyLinkDataSource(this.sourceAgencyLink)
            .primaryAgencyField(this.getCarrierUsersAgencyField())
            .build();
        urs.show();
    }

    private getCarrierUsersSlideoutTitle(): string {
        const name = this.sourceCarrier.activeRow?.get("payee.name");
        return "Carrier Users" + ((StringUtil.isEmptyString(name) === false) ? (" - " + name) : "");
    }

    private getCarrierUsersIdFilter(): any {
        const id = this.sourceCarrier.activeRow?.get("id");
        if (id != null)
            return { id: id };
        return null;
    }

    private getCarrierUsersAgencyField(): string {
        return "payee.primary_agency";
    }

    sourceCarrierAfterGetDataboundValues(row: ModelRow, dataSource: DataSource) {
        if (dataSource.mode === DataSourceMode.SEARCH) {
            //have to set primary_agency in search mode because the field isn't bound
            const primaryAgencyField = this.getCarrierUsersAgencyField();
            row.set(primaryAgencyField, dataSource.searchRow.get(primaryAgencyField));

            //have to add agency_link search data to the filter manually, since there are no bound components for those records
            if (ArrayUtil.isEmptyArray(this.sourceAgencyLink.data))
                return;
            row.addLinkedModel({ model: this.sourceAgencyLink.url, rows: [this.sourceAgencyLink.data[0]] });
        }
    }

    tableTrailerTypesOnRowDisplay(event) {
        const tableRow = event.getTableRow();
        const lableDescr = tableRow.findComponentById("labelDescr") as Label;
        if (tableRow.mode === TableRowMode.QUICK_ADD && tableRow.data?.get("default_trailer") == null && this.mainDataSource.mode != null) {
            const textboxSendToDriver = tableRow.findComponentById("textboxDefaultTrailer") as Textbox;
            textboxSendToDriver.setSelectedItemFromValue("N");
            textboxSendToDriver.updateBoundData(tableRow.data, tableRow.table.dataSource.mode);
        } else {
            if (tableRow.data.getLookupModelData("equipment_type_id") != null) {
                lableDescr.text = tableRow.data.getFirstLookupModelData("equipment_type_id")?.get("descr");
            }
        }
        const textboxEquipmentTypeId = tableRow.findComponentById("textboxEquipmentTypeId") as Textbox;
        textboxEquipmentTypeId.addLookupModelSelectionListener(this.textboxEquipmentTypeIdOnLookupModelSelection);
        textboxEquipmentTypeId.addBlurListener(event => {
            if (event.changedWhileFocused && StringUtil.isEmptyString(textboxEquipmentTypeId.text))
                lableDescr.text = textboxEquipmentTypeId.boundRow.getFirstLookupModelData("equipment_type_id")?.get("descr");
        });

    }

    private textboxEquipmentTypeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const labelDescr = TableRow.getContainingTableRow(event.target)?.findComponentById("labelDescr") as Label;
            if (labelDescr != null)
                labelDescr.text = event.target.boundRow.getFirstLookupModelData("equipment_type_id")?.get("descr");
        }
    }

    async sourceCarrierAfterExecution(event: DataSourceExecutionEvent) {
        if (this.sourceCarrier.activeRow != null) {
            this.layoutLanePreferenceTable.payeeId = this.activeRow.get("id");
            this.layoutLaneSendToContacts.payeeId = this.activeRow.get("id");
            this.layoutCarrierActionList.carrierId = this.activeRow.get("id");
            this.layoutManageCarrierCapacity.carrierId = this.activeRow.get('id');
            this.layoutCarrierOfferList.carrierName = this.activeRow.get("payee.name");
            this.syncPnnOptions(this.activeRow.getBoolean("pnn_carrier.is_active", false));
            if (event.getAction() === DataSourceAction.SEARCH) {
                (this.layoutImageTable as ImageTable).populateForRow("P", this.activeRow.get("id"));
                this.updateCimVendorUrl();
                this.checkForMacropointVendor();
            }

            if (!StringUtil.isEmptyString(this.activeRow?.get("active_vendor_name"))) {
                this.buttonMonitorCarrier.tooltip = "Get the latest carrier updates from " + this.activeRow?.get("active_vendor_name");
                this.buttonMonitorCarrier.visible = true;
            } else{
                this.buttonMonitorCarrier.visible = false;
            }

            if (!StringUtil.isEmptyString(this.activeRow?.get("active_vendor_name")) && this.setVendorSpecificFields(this.activeRow?.get("active_vendor_name"))) {
                this.labelVendor.visible = true;
                this.labelVendor.text = this.activeRow?.get("active_vendor_name") + " Details";

                if (this.activeRow?.getBoolean("is_highway_active_vendor", false)) {
                    this.textboxHighwayIdNumber.visible = this.activeRow?.getBoolean("is_highway_active_vendor", false);
                    this.textboxHighwayRuleAssessment.visible = this.activeRow?.getBoolean("is_highway_active_vendor", false);
                    this.textboxHighwayStatus.visible = this.activeRow?.getBoolean("is_highway_active_vendor", false);
                } else if(StringUtil.equalsIgnoreCase("active_vendor_name","MyCarrierPortal")) {
                    this.switchIsCertifiedRMI.visible = this.activeRow?.getBoolean("is_certified_visible", false);
                    this.switchWatchdogReport.visible = this.activeRow?.getBoolean("watchdog_report_visible", false);
                    this.textboxOverAllRisk.visible = this.activeRow?.getBoolean("overall_risk_assessment_visible", false);

                    this.textboxHighwayIdNumber.visible = false;
                    this.textboxHighwayRuleAssessment.visible = false;
                    this.textboxHighwayStatus.visible = false;
                }
            } else {
                this.labelVendor.visible = false;
                this.switchIsCertifiedRMI.visible = false;
                this.switchWatchdogReport.visible = false;
                this.textboxOverAllRisk.visible = false;
                this.textboxHighwayIdNumber.visible = false;
                this.textboxHighwayRuleAssessment.visible = false;
                this.textboxHighwayStatus.visible = false;
            }

            Api.search("lme/powerbroker/carrier-qualifications", { "payee_id": this.sourceCarrier.activeRow.get("id") }).then(result => {
                const carrierValidationData = result?.data[0];
                const passed = carrierValidationData?.passed || false;
                const warning = carrierValidationData?.warning || false;
                this.tabQualifications.imageName = (passed && warning) ? ImageName.ribbonWarning : passed ? ImageName.ribbonCheck : ImageName.ribbonAlert;
                const tabHeader = this.tabQualifications?.["_heading"];
                if (tabHeader != null)
                    tabHeader.tooltip = passed ? "Carrier passed all qualifications \nfor the carrier qualification profile." : "Carrier is disqualified based on\nthe carrier qualification profile.";
                (this.layoutQualification as CarrierQualification).setQualificationData(carrierValidationData);
                this.configureBtnOverrideCarrier();
            });


            // Always reset insurance tab visibility before checking vendor
            this.tabsetAdditionalInsurance.visible = false;
            if (this.activeRow?.getBoolean("is_highway_active_vendor", false)) {
               await this.sourceCarrierInsurance.search({"payee_id": this.sourceCarrier.activeRow.get("id")});

                   const generalInsuranceInfo = this.sourceCarrierInsurance.activeRow?.get("general_insurance_information");
                   const trailerInsuranceInfo = this.sourceCarrierInsurance.activeRow?.get("trailer_insurance_information");
                   const workersCompInsuranceInfo = this.sourceCarrierInsurance.activeRow?.get("workers_insurance_information");
                   const contingentAutoInsuranceInfo = this.sourceCarrierInsurance.activeRow?.get("contingent_auto_insurance_information");
                   const contingentCargoInsuranceInfo = this.sourceCarrierInsurance.activeRow?.get("contingent_cargo_insurance_information");

                   if (generalInsuranceInfo) {
                       this.populateInsurancePanels(this.generalLiabilityInsurancePanel, generalInsuranceInfo, "general_insurance");
                   }
                   if (trailerInsuranceInfo) {
                       this.populateInsurancePanels(this.interchangeInsurancePanel, trailerInsuranceInfo, "trailer_insurance");
                   }
                   if (workersCompInsuranceInfo) {
                       this.populateInsurancePanels(this.workersCompInsurancePanel, workersCompInsuranceInfo, "workers_comp_insurance");
                   }
                   if (contingentAutoInsuranceInfo) {
                       this.populateInsurancePanels(this.contAutoLiabilityPanel, contingentAutoInsuranceInfo, "contingent_auto_insurance_information");
                   }
                   if (contingentCargoInsuranceInfo) {
                       this.populateInsurancePanels(this.contCargoLiabilityPanel, contingentCargoInsuranceInfo, "contingent_cargo_insurance_information");
                   }

                   if(generalInsuranceInfo || trailerInsuranceInfo || workersCompInsuranceInfo || contingentAutoInsuranceInfo || contingentCargoInsuranceInfo){
                       this.tabsetAdditionalInsurance.visible = true;
                   }
            }



            if (!this.activeRow.isNull('mark_plan_id')) {
                this.textboxCarrierPlan.visible = true;
                this.textboxStartDate.visible = true;
                new ModelCarrierPlan().searchSingle({'id':this.activeRow.getString('mark_plan_id', null)})
                .then(row => CommonTooltips.setTooltipContentLayout(this.textboxCarrierPlan, row, 'lme/crm/CarrierPlanQuickInfo'));
            } else {
                this.textboxCarrierPlan.visible = false;
                this.textboxStartDate.visible = false;
            }

            this.tabIsoScorecard.visible = this.sourceCarrier.activeRow.get("is_iso_active_and_licensed") && StringUtil.equalsIgnoreCase(this.sourceCarrier.activeRow.get("carrier_scorecard_exported"), "Y");
            this.tabCarrierInsights.visible = this.activeRow?.getBoolean("show_carrier_insights", false);
            this.layoutTableDrivers.setEnableTrackingFieldsVisibility(this.sourceCarrier.activeRow?.getBoolean("enable_international_tracking", false));
            const resetButtonOffers = this.layoutCarrierOfferList.searchpanelSearchOffers["buttonResetForm"] as ResetFormButton;
            if (resetButtonOffers) {
                const resetCallback = resetButtonOffers.doAfterReset;
                resetButtonOffers.doAfterReset = (event: ClickEvent) => {
                    resetCallback?.(event);
                    this.layoutCarrierOfferList.sourceCarrierOfferList.searchRow.setValueAndLookupModelData(
                        "carrier_name",
                        this.activeRow.get("payee.name"),
                        {"name": this.activeRow.get("payee.name")}
                    );
                    this.layoutCarrierOfferList.textboxCarrierName.displayComponentData(
                        this.layoutCarrierOfferList.sourceCarrierOfferList.searchRow, null, 0
                    );

                    const defaultDateRange = DateRange.parseDateRangeWithKeywords("t-30,t");
                    this.layoutCarrierOfferList.textboxDateRange.text = DisplayValue.getDisplayValue(
                        defaultDateRange,
                        DisplayType.DATERANGE,
                        this.layoutCarrierOfferList.textboxDateRange.format
                    );
                    this.layoutCarrierOfferList.sourceCarrierOfferList.searchRow.set("offer_date", defaultDateRange);

                    this.layoutCarrierOfferList.textboxCarrierName.enabled = false;
                };
            }
            this.layoutCarrierOfferList.textboxCarrierName.enabled = false;
            this.layoutCarrierOfferList.buttonViewInNewTab.visible = true;
            this.layoutCarrierOfferList.tableOffers.defaultOpenType = OpenType.NEW_TAB_WITH_SWITCH;
            this.hideShowCPTATFields(this.switchCTPAT);
        }
    }

    sourceCarrierPerfCalculationAfterExecution(event: DataSourceExecutionEvent) {
        this.labelDash.visible = this.sourceCarrierPerfCalculation.activeRow?.get("date_range_start") !== null;
    }

    tableScoreCardDetailsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        if (modelRow.get("star_ratio", null) != null) {
           const starratingPercent = tableRow.findComponentById("starratingPercent");
           const textboxPercent = tableRow.findComponentById("textboxPercent");
           if (starratingPercent) {
               starratingPercent.visible = true;
           }
           if (textboxPercent) {
               textboxPercent.visible = false;
           }
        }
        else {
            const starratingPercent = tableRow.findComponentById("starratingPercent");
            const textboxPercent = tableRow.findComponentById("textboxPercent");
            if (starratingPercent) {
                starratingPercent.visible = false;
            }
            if (textboxPercent) {
                textboxPercent.visible = true;
            }
        }

    }

    buttonViewResultsOnClick(event: ClickEvent) {
        Navigation.navigateTo(`lme/powerbroker/CarrierScorecardHistory?carrierId=${this.mainDataSource.activeRow.get("id")}`, {newTab: true});
    }

    labelDashboardLinkOnClick(event: ClickEvent) {
        this.sourceCarrierScorecard.search({ "is_active": "Y" }).then(result => {
            const modelRow: ModelRow[] = result.modelRows;
            if (!modelRow || !modelRow[0]?.data || modelRow[0]?.data?.length === 0) {
                return;
            }

            const baseUrl = modelRow[0]?.data.iso_portal_url + "/carriers?";
            const activeRow = this.sourceCarrier.activeRow;

            if (activeRow) {
                const iccNumber = activeRow.get("icc_number");
                const dotNumber = activeRow.get("dot_number");

                const queryParam = !StringUtil.isEmptyString(iccNumber)
                    ? `mc_number=${iccNumber}`
                    : !StringUtil.isEmptyString(dotNumber)
                        ? `dot_number=${dotNumber}`
                        : null;

                if (queryParam) {
                    window.open(baseUrl + queryParam, "_blank");
                }
            }
        });
    }


    tabsetMainAfterTabSelection(event) {
        if (event.newSelection.element.id == this.tabCarrierCapacity.id
        && this.layoutManageCarrierCapacity.sourceAvailTractDetail.lastSearch == null
        && this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            this.layoutManageCarrierCapacity.mainDataSource.search({"carrier_id": this.activeRow.get("id"), "only_active": true});
        }
    }

    public static navigateTo(id: string): void {
        Navigation.navigateTo(this.viewCarrierURL + id, { newTab: true });
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.brdsp.EntryCarrierPayee", this.mainDataSource.activeRow.get("id"));
    }

    buttonCallinHistoryOnClick(event: ClickEvent) {
        const button = event.target as Button;
        button.busy = true;
        const layout = Layout.getLayout("lme/dispatch/CarrierCallinHistoryList") as AvailableLoadsList;
        layout.addLayoutLoadListener(() => {
            const id = this.activeRow.get("id", null);
            const name = this.activeRow.getString("payee.name", null);
            const lmData = new ModelRow("lme/powerbroker/carrier-payee", false, { id, name });
            layout.mainDataSource.searchRow.setValueAndLookupModelData("payee_id", id, lmData);
            layout.mainDataSource.searchRow.set("call_date_time", "t-60,t");
            layout.mainDataSource.displayDataInBoundSearchComponents();
            layout.mainDataSource.executeValidatedSearch();
        });
        new SlideoutDecorator({
            layout: layout,
            fillVerticalSpace: true,
            fillHorizontalSpace: true,
            transitionOptions: { direction: Alignment.BOTTOM },
            title: "Carrier Callin History - " + this.activeRow.get("payee.name", null),
        });
        button.busy = false;
    }

    buttonLaneHistoryOnClick(event: ClickEvent) {
        const laneHistoryLayout: LaneHistory = Layout.getLayout("lme/dispatch/LaneHistory") as LaneHistory;
        new SlideoutDecorator({
            layout: laneHistoryLayout,
            layoutLoadListeners: async (event) => {
                laneHistoryLayout.layoutheaderLaneHist.visible = false;
                const lmData = new ModelRow(laneHistoryLayout.textboxOverridePayeeId.lookupModel, false,
                    {
                        id: this.mainDataSource.activeRow.get("id"),
                        name: this.mainDataSource.activeRow.get("payee.name"),
                        phone_number: this.mainDataSource.activeRow.get("payee.phone_number"),
                        icc_number: this.mainDataSource.activeRow.get("icc_number"),
                        dot_number: this.mainDataSource.activeRow.get("dot_number")
                    });

                laneHistoryLayout.mainDataSource.searchRow.setValueAndLookupModelData("override_payee_id", this.mainDataSource.activeRow.get("id"), lmData);
                laneHistoryLayout.textboxOverridePayeeId.displayData(laneHistoryLayout.mainDataSource.searchRow, null, 0);
                laneHistoryLayout.payeeSelection(this.mainDataSource.activeRow.get("id"));


                const defaultDateRange = DateRange.parseDateRangeWithKeywords("t-30,t");
                laneHistoryLayout.textboxDateRange.text = DisplayValue.getDisplayValue(defaultDateRange, DisplayType.DATERANGE, laneHistoryLayout.textboxDateRange.format);
                laneHistoryLayout.sourceLaneHistory.executeValidatedSearch();
            },
            fillVerticalSpace: true,
            fillHorizontalSpace: true,
            title: "Lane History",
            transitionOptions: { direction: Alignment.CENTER },
            overlayProps: { closeOnClickOff: false, greyedBackground: true }
        });
    }


    switchCTPATOnChange(event: ChangeEvent) {
        const switchCTPAT = event.target as Switch;
        this.hideShowCPTATFields(switchCTPAT);
    }

    hideShowCPTATFields(switchCTPAT: Switch) {
        this.textboxSVI.visible = this.textboxClass.visible = switchCTPAT.checked;
    }


    buttonMonitorUpdateOnClick(event: ClickEvent) {
        const defaultImage = ImageName.defaultMonitorUpdate;
        const activeImage = ImageName.activeMonitorUpdate;
        let toggle = true;

        const intervalId = setInterval(() => {
            this.buttonMonitorCarrier.imageName = toggle ? defaultImage : activeImage;
            toggle = !toggle;
        }, 200);

        Api.post("lme/dispatch/update-carrier",
            {
                dot_number: this.textboxDotNumber.text,
                mc_number: this.textboxIccNumber.text,
                intrastate_code: this.textboxIntrastateCode.text,
                intrastate_state: this.textboxIntrastateState.text}
        ).then(() => {
            clearInterval(intervalId);

            this.mainDataSource.search(this.mainDataSource.lastSearch).then(() => {
            setTimeout(() => Toast.showSuccessToast('Success!', `Carrier record updated.`), 500);
            });
        }).catch(error => {
            clearInterval(intervalId);
            this.buttonMonitorCarrier.imageName = defaultImage;
            Snackbar.showWarningSnackbar(error?.messages[0]);
        });
    }

    public async getTdsUrl(dotNumber: string) {
        return Api.post("lme/powerbroker/get-carrier-monitor-url", { "vendor_id": "P", "dot_number": dotNumber })
            .then(response => {
                const url = response.data[0]?.url;
                const errorMessage = response.data[0]?.error_msg;
                if (!StringUtil.isEmptyString(errorMessage)) {
                    Snackbar.showWarningSnackbar("DOT Number is required to access monitoring service website for this carrier");
                    return "";
                } else {
                    return url;
                }
            });
    }

    checkForMacropointVendor() {
        if (this.activeRow.getBoolean("has_macropoint_br_track_ctrl", false)) {
            this.switchMacroPointOnboarded.visible = true;
            this.textboxTrackingMethod.visible = true;
        }
    }

    updateCimVendorUrl() {
        const vendorId = this.activeRow.get("ins_ctrl_vendor_id");
        const validVendorIds = ['D', 'H', 'S', 'P'];
        this.btnCimVendorUrl.visible = validVendorIds.includes(vendorId);

        if (!this.btnCimVendorUrl.visible) {
            return;
        }

        let tooltip: string;
        // Set tooltip based on vendor ID
        switch (vendorId) {
            case 'D':
                tooltip = 'MyCarrierPortal Website';
                break;
            case 'H':
                tooltip = 'Highway Website';
                this.tabCustomClassifications.visible = true;
                break;
            case 'S':
                tooltip = 'SaferWatch Website';
                break;
            case 'P':
                tooltip = 'Transportation Data Source Website';
                break;
        }
        this.btnCimVendorUrl.tooltip = tooltip;
    }

    /** This is an event handler for the onClick event of btnCimVendorUrl. */
    btnCimVendorUrlOnClick(event: ClickEvent) {
        let webUrl: string = "";
        let mcNumber = this.activeRow?.get("icc_number");
        const vendorId = this.activeRow.get("ins_ctrl_vendor_id");
        if (!StringUtil.isEmptyString(mcNumber) && mcNumber.length > 2 && mcNumber.substring(0, 2) !== "MC") {
            mcNumber = "MC" + mcNumber;
        }
        const dotNumber = this.activeRow?.get("dot_number");

        if (vendorId === "D") {
            webUrl = 'https://mycarrierpackets.com/CarrierInformation';
            if (!StringUtil.isEmptyString(dotNumber)) {
                webUrl = webUrl + '/DotNumber/' + dotNumber;
            } else if (!StringUtil.isEmptyString(mcNumber)) {
                webUrl = webUrl + '/DocketNumber/' + mcNumber;
            } else {
                webUrl = "https://mycarrierpackets.com";
            }

            window.open(webUrl, '_blank');
        } else if (vendorId === "H") {
            const host = this.activeRow.get("ins_ctrl_host");
            webUrl = host + '/broker';
            const highwayIdNumber = this.mainDataSource.activeRow.get("highway_id_number");
            if (!StringUtil.isEmptyString(highwayIdNumber)) {
                webUrl = webUrl + '/carriers/' + highwayIdNumber;
            }
            window.open(webUrl, '_blank');
        } else if (vendorId === "S") {
            webUrl = 'http://www.saferwatch.com/swCarrierDetailsLink.php?';
            if (!StringUtil.isEmptyString(dotNumber)) {
                webUrl = webUrl + '&dotNumber=' + dotNumber;
            } else if (!StringUtil.isEmptyString(mcNumber)) {
                webUrl = webUrl + '&docketNumber=' + mcNumber;
            }
            window.open(webUrl, '_blank');
        } else if (vendorId === "P") {
            this.getTdsUrl(dotNumber).then(url => {
                if (!StringUtil.isEmptyString(url)) {
                            window.open(url, '_blank');
                }
            });
        }
    }

    /** This is an event handler for the beforeModeChange event of sourceCarrier.  */
    async sourceCarrierBeforeModeChange(event) {
        this.dataheaderCarrier.addlPanelLeft.forEveryChildComponent(comp => {
            if (comp instanceof Button && comp.id !== "btnCimVendorUrl") {
                comp.visible = event.newMode !== DataSourceMode.SEARCH;
            }
        });
    }

    textboxCategoryBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.carrier = "Y";
    }

    checkExpiryDate(comp: Textbox) {
        let value: string | number = comp.text;
        const today = new Date().setUTCHours(0, 0, 0, 0);
        value = new Date(value).setUTCHours(0, 0, 0, 0)
        if (comp.validationWarning == null) {
            const expired: boolean = value < today;
            comp.color = expired ? "warning" : "default";
            comp.borderWidth = expired ? 1 : undefined;
            comp.borderColor = expired ? "warning" : undefined;
            comp.tooltip = expired ? "The expiry date is in the past." : undefined;
        }
    }

    /** This is an event handler for the onBlur event of textboxLiabilityExpDate.  */
    textboxLiabilityExpDateOnBlur(event: BlurEvent) {
        const component = event.target as Textbox;
        this.checkExpiryDate(component);
    }

    /** This is an event handler for the onBlur event of textboxCargoExpDate.  */
    textboxCargoExpDateOnBlur(event: BlurEvent) {
        const component = event.target as Textbox;
        this.checkExpiryDate(component);
    }

    /** This is an event handler for the afterModeChange event of sourceCarrier.  */
    sourceCarrierAfterModeChange(event: DataSourceModeChangeEvent) {
        this.checkExpiryDate(this.textboxCargoExpDate);
        this.checkExpiryDate(this.textboxLiabilityExpDate);
        if (event.newMode === DataSourceMode.SEARCH || event.newMode === DataSourceMode.ADD) {
            this.tabImages.visible = false;
        }

        const isNotSearchMode = event.newMode !== DataSourceMode.SEARCH;
        this.textboxCarrierPlan.printable = isNotSearchMode;
        this.textboxCarrierPlan.nullDisplayValue = isNotSearchMode ? "hide" : "";
        this.textboxStartDate.printable = isNotSearchMode;
        this.layoutManageCarrierCapacity.visible = isNotSearchMode;
        this.labelDashboardLink.visible = isNotSearchMode;
        this.buttonLaneHistory.visible = isNotSearchMode;
        this.textboxRecentRanking.enabled = !isNotSearchMode;
    }

    /** This is an event handler for the onClick event of buttonOverrideOrder.  */
    buttonOverrideCarrierOnClick(event: ClickEvent) {
        CarrierOverride.show({ payee_id: this.activeRow.get("id") });
    }

    /** This is an event handler for the onChange event of switchRequirementDispatchOverride.  */
    switchRequirementDispatchOverrideOnChange(event) {
        this.configureBtnOverrideCarrier();
    }

    /** This is an event handler for the onChange event of switchOverrideQualificationEnforcement.  */
    switchOverrideQualificationEnforcementOnChange(event) {
        this.configureBtnOverrideCarrier();
    }

    /** This is an event handler for the onChange event of textboxOverrideQualificationDate.  */
    textboxOverrideQualificationDateOnChange(event) {
        this.buttonOverrideCarrier.visible = false;

        if (this._ovrrdTimeoutHandle != null)
            window.clearTimeout(this._ovrrdTimeoutHandle);
        this._ovrrdTimeoutHandle = window.setTimeout(() => {
            this.configureBtnOverrideCarrier();
        }, this.textboxOverrideQualificationDate.lookupModelInputDelay);

    }

    configureBtnOverrideCarrier() {
        this.buttonOverrideCarrier.visible = this.showCarrierOverride();
    }

    showCarrierOverride(): boolean {
        if (this.activeRow == null)
            return false;

        if (!PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_CARRIER_DISPATCH_OVERRIDE)) {
            if (this.activeRow.getBoolean("override_qual_enf")
                && getDispatchControlBoolean("enable_authorized")
                && (this.activeRow.isNull("override_qual_date") || this.isQualifiedDate())
            ) {
                return true;
            }

            if (this.activeRow.getBoolean("req_dispatch_over")) {
                return true;
            }
        }

        return false;
    }

    isQualifiedDate(): boolean {
        const validationResult = TextboxValidator.validate(this.textboxOverrideQualificationDate, true, false);
        if (validationResult != null && !validationResult.isValid)
            return false;

        const ovrrdQualDate = new Date(this.activeRow.get("override_qual_date")).setUTCHours(0, 0, 0, 0);
        const currentDate = new Date().setUTCHours(0, 0, 0, 0);

        return ovrrdQualDate > currentDate;

    }


    populateInsurancePanels(panel: Panel, insuranceData: InsuranceInfo, insuranceType: string) {
        if (StringUtil.isEmptyString(insuranceType)) {
            return;
        }

        const USDollar = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        });

        if (StringUtil.equalsIgnoreCase("trailer_insurance", insuranceType)) {
            panel.visible = true;
            if (!StringUtil.isEmptyString(insuranceData.expiration_date)) {
                const date = this.formatExpirationDate(insuranceData.expiration_date);
                this.textboxInterchangeExpirationDate.text = date;
                this.textboxTrailerCancelDate.text = date;
            }
            this.textboxTrailerAmount.text = USDollar.format(insuranceData.insurance_amount);
            this.textboxTrailerCompany.text = insuranceData.insurer_name;
            this.textboxTrailerPolicyNum.text = insuranceData.insurance_policy_number;
            this.textboxTrailerPhone.text = insuranceData.insurer_phone;
        } else if (StringUtil.equalsIgnoreCase("general_insurance", insuranceType)) {
            panel.visible = true;
            if (!StringUtil.isEmptyString(insuranceData.expiration_date)) {
                const date = this.formatExpirationDate(insuranceData.expiration_date);
                this.generalLiabilityExpirationDate.text = date;
                this.generalLiabilityCancellationDate.text = date;
            }
            this.generalLiabilityAmount.text = USDollar.format(insuranceData.insurance_amount);
            this.generalLiabilityCompany.text = insuranceData.insurer_name;
            this.generalLiabilityPolicyNum.text = insuranceData.insurance_policy_number;
            this.generalLiabilityPhone.text = insuranceData.insurer_phone;
        } else if (StringUtil.equalsIgnoreCase("workers_comp_insurance", insuranceType)) {
            panel.visible = true;
            if (!StringUtil.isEmptyString(insuranceData.expiration_date)) {
                const date = this.formatExpirationDate(insuranceData.expiration_date);
                this.textboxWorkersCompExpirationDate.text = date;
                this.textboxWorkersCompCancellationDate.text = date;
            }
            this.textboxWorkersCompAmount.text = USDollar.format(insuranceData.insurance_amount);
            this.textboxWorkersCompCompany.text = insuranceData.insurer_name;
            this.textboxWorkersCompPolicyNum.text = insuranceData.insurance_policy_number;
            this.textboxWorkersCompPhone.text = insuranceData.insurer_phone;

        } else if (StringUtil.equalsIgnoreCase("contingent_auto_insurance_information", insuranceType)) {
            panel.visible = true;
            if (!StringUtil.isEmptyString(insuranceData.expiration_date)) {
                const date = this.formatExpirationDate(insuranceData.expiration_date);
                this.textboxContAutoExpirationDate.text = date;
                this.textboxContAutoCancellationDate.text = date;
            }
            this.textboxContAutoAmount.text = USDollar.format(insuranceData.insurance_amount);
            this.textboxContAutoCompany.text = insuranceData.insurer_name;
            this.textboxContAutoPolicyNum.text = insuranceData.insurance_policy_number;
            this.textboxContAutoPhone.text = insuranceData.insurer_phone;

        } else if (StringUtil.equalsIgnoreCase("contingent_cargo_insurance_information", insuranceType)) {
            panel.visible = true;
            if (!StringUtil.isEmptyString(insuranceData.expiration_date)) {
                const date = this.formatExpirationDate(insuranceData.expiration_date);
                this.textboxContCargoExpirationDate.text = date;
                this.textboxContCargoCancellationDate.text = date;
            }
            this.textboxContCargoAmount.text = USDollar.format(insuranceData.insurance_amount);
            this.textboxContCargoCompany.text = insuranceData.insurer_name;
            this.textboxContCargoPolicyNum.text = insuranceData.insurance_policy_number;
            this.textboxContCargoPhone.text = insuranceData.insurer_phone;
        }
    }

    formatExpirationDate(expiration_date: string) {
        const date = new Date(expiration_date);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return [day, month, year].join('/');
    }

    setMacroPointOnlyFields() {
        new ModelBrokerageTrackingCtrl().search().then((result: ModelSearchResult) => {
            const data: ModelRow[] = result.modelRows;
            data.forEach((row: ModelRow) => {
                if (StringUtil.equalsIgnoreCase(row?.get("vendor"), "M")) {
                    this.switchMacroPointOnboarded.visible = true;
                    this.textboxTrackingMethod.visible = true;
                }
            });
        });
    }


    textboxP44ProfileBeforeLookupModelSearch(event) {
        event.filter.config_type = 'Y';
    }

    textboxSMC3ProfileIdBeforeLookupModelSearch(event) {
        event.filter.config_type = 'Y';
    }

    tableInsightsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        const row = tableRow.data;
        tableRow.visible = !StringUtil.isEmptyString(row.get("insight"));
    }

    switchActivePNNCarrierOnChange(event: ChangeEvent) {
        this.syncPnnOptions(event.newValue);
    }

    switchLanePreferenceDisplayOnChange(event: ChangeEvent) {
        this.layoutLanePreferenceTable.visible = event.newValue;
        this.layoutLaneSendToContacts.visible = !event.newValue;
        this.contactManager.syncLaneContactsAndPreferences();
    }

    syncPnnOptions(isActive: boolean) {
        this.panelPnnOptions.visible = this.panelPnnComments.visible = isActive;
        this.switchActivePNNCarrier.paddingTop = isActive ? 0 : 13;
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.color = "subtle.light";
            tab.headingLabel.setProps({ color: "default", fontBold: true });
        }
    }

    private setVendorSpecificFields(activeVendorName: string) {
        return StringUtil.equalsIgnoreCase(this.activeRow?.get("active_vendor_name"),"Highway")
            || StringUtil.equalsIgnoreCase(this.activeRow?.get("active_vendor_name"),"MyCarrierPortal");
    }

    buttonCarrierPlanOnClick(event: ClickEvent) {
        const payeeId = this.mainDataSource.activeRow.get("id");
        AssignCarrierPlanPrompt.showModal(payeeId);
    }

    buttonAvailableLoadsOnClick(event: ClickEvent) {
        const button = event.target as Button;
        button.busy = true;

        const layout = Layout.getLayout("lme/crm/AvailableLoadsList") as AvailableLoadsList;
        layout.addLayoutLoadListener(() => {
            layout.carrierId = this.activeRow.getString("id");
            layout.textboxDefaultContact.text = this.activeRow.getString("contact.name", null);
            layout.textboxPhone.text = this.activeRow.getString("contact.phone", null);
            layout.textboxEmail.text = this.activeRow.getString("contact.email", null);
        });

        new SlideoutDecorator({
            layout: layout,
            fillVerticalSpace: true,
            fillHorizontalSpace: true,
            transitionOptions: { direction: Alignment.BOTTOM },
            title: "Carrier Callin History"
        });

        button.busy = false;
    }

    buttonQualDetailsOnClick(event: ClickEvent) {
        const payeeId = this.mainDataSource.activeRow.get("id");
        QualificationDetailSlideout.showSlideout(payeeId);
    }

    buttonViewPayeeOnClick(event: ClickEvent) {
        Navigation.navigateTo(`lme/settlement/DrsPayee?mode=update&key=${this.activeRow.get("id")}`, { newTab: true });
    }

    switchExcludeCarrierOnChange() {
        this.textboxMpwCarrierContactMethod.visible = !this.switchExcludeCarrier.checked;
        this.textboxMpwCarrierHigh.visible = !this.switchExcludeCarrier.checked;
        this.textboxMpwCarrierLow.visible = !this.switchExcludeCarrier.checked;
        this.textboxMpwNumOrders.visible = !this.switchExcludeCarrier.checked;
        this.textboxMpwNumAttempts.visible = !this.switchExcludeCarrier.checked;
        this.textboxMpwLastContact.visible = !this.switchExcludeCarrier.checked;
    }

    private setRecurringTractorLastCreatedTooltip() {
        this.textboxRecurTractorLastCreated.tooltipDynamicContent = () => {
            const link = LayoutUrl.create(
                "lme/dispatch/RecurringAvailableTractorList",
                {id: this.activeRow?.get("id")},
            );
            return link == null ? null : new Label({ text: "Recurring Tractor List", link });
        };
    }

}
